import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/components/ui/use-toast";
import { Loader2, CreditCard, AlertTriangle } from "lucide-react";

interface HavGuest {
  first_name: string;
  last_name: string;
  seat_category: string;
}

interface HavRegistration {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  sponsor_name: string;
  seat_category: string;
  amount: number;
  seats_count: number;
  payment_status: string;
  guests: HavGuest[];
}

const catLabel = (c: string) => (c === "orchestre" ? "Orchestre" : "Balcon");

export default function HautVoixPaiement() {
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const navigate = useNavigate();
  const registrationId = searchParams.get("id");

  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [registration, setRegistration] = useState<HavRegistration | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!registrationId) {
        setError("Identifiant d'inscription manquant");
        setIsLoading(false);
        return;
      }
      try {
        const { data, error: rpcError } = await supabase.rpc(
          "get_hav_registration_for_payment",
          { registration_id: registrationId } as any
        );
        if (rpcError) throw rpcError;
        const results = data as any[];
        if (!results || results.length === 0) {
          throw new Error("Préinscription non trouvée ou déjà réglée");
        }
        setRegistration(results[0] as HavRegistration);
      } catch (err: any) {
        setError(err.message || "Une erreur est survenue");
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [registrationId]);

  const handlePayment = async () => {
    if (!registration) return;
    setIsProcessing(true);
    try {
      const result = await supabase.functions.invoke("create-hav-payment", {
        body: { registrationId: registration.id },
      });
      if (result.error) throw result.error;
      const data = result.data as any;

      if (data?.free) {
        navigate(`/hav-success?registration_id=${registration.id}`, { replace: true });
        return;
      }
      if (data?.sessionUrl) {
        window.location.href = data.sessionUrl;
        return;
      }
      throw new Error("Impossible de créer la session de paiement");
    } catch (err: any) {
      console.error("HAV payment error:", err);
      toast({
        variant: "destructive",
        title: "Erreur de paiement",
        description: "Impossible de procéder au paiement. Veuillez réessayer.",
      });
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFF200] flex items-center justify-center px-4 py-10">
      <div className="max-w-xl w-full">
        <h1 className="text-3xl font-extrabold italic uppercase text-[#0A1A6B] text-center mb-2">
          À Haute Voix
        </h1>
        <p className="text-center text-[#0A1A6B]/80 mb-6">
          Jeudi 5 novembre 2026 — Théâtre de la Tour Eiffel, Paris 7ᵉ
        </p>
        <Card className="border-0 shadow-2xl">
          <CardContent className="pt-6">
            {isLoading ? (
              <div className="text-center py-8">
                <Loader2 className="h-10 w-10 text-[#0A1A6B] animate-spin mx-auto" />
                <p className="mt-2 text-muted-foreground">Chargement...</p>
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <AlertTriangle className="h-10 w-10 text-destructive mx-auto mb-2" />
                <h2 className="text-xl font-bold text-destructive mb-2">Erreur</h2>
                <p className="text-muted-foreground">{error}</p>
                <Button className="mt-4" onClick={() => navigate("/inscription-AHV2026")}>
                  Retour à l'inscription
                </Button>
              </div>
            ) : (
              registration && (
                <>
                  <h2 className="text-2xl font-bold text-[#0A1A6B] text-center mb-6">
                    Finaliser votre inscription
                  </h2>
                  <div className="space-y-4">
                    <div className="bg-muted/50 p-4 rounded-lg space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="font-medium">Participant principal</span>
                        <span>
                          {registration.first_name} {registration.last_name} —{" "}
                          {catLabel(registration.seat_category)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium">Email</span>
                        <span>{registration.email}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium">Parrain</span>
                        <span>{registration.sponsor_name}</span>
                      </div>
                      {(registration.guests || []).map((g, i) => (
                        <div key={i} className="flex justify-between">
                          <span className="font-medium">Participant {i + 2}</span>
                          <span>
                            {g.first_name} {g.last_name} — {catLabel(g.seat_category)}
                          </span>
                        </div>
                      ))}
                    </div>
                    <Separator />
                    <div className="bg-muted/50 p-4 rounded-lg flex justify-between font-bold text-lg">
                      <span>
                        Total ({registration.seats_count} place
                        {registration.seats_count > 1 ? "s" : ""})
                      </span>
                      <span>{registration.amount} €</span>
                    </div>
                    <Button
                      onClick={handlePayment}
                      disabled={isProcessing}
                      className="w-full py-6 bg-[#0A1A6B] hover:bg-[#06124d] text-white text-lg"
                    >
                      {isProcessing ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Traitement...
                        </>
                      ) : (
                        <>
                          <CreditCard className="mr-2 h-4 w-4" />
                          {registration.amount > 0
                            ? `Payer ${registration.amount} €`
                            : "Valider mon inscription (0 €)"}
                        </>
                      )}
                    </Button>
                  </div>
                </>
              )
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
